import React, { useState, useEffect } from "react";
import {
  Briefcase,
  Network,
  Database,
  CloudCog,
  Settings,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

export default function NeighborhoodProtectionFeatures() {
  const [flippedCard, setFlippedCard] = useState(null); // current open card
  const [closingCard, setClosingCard] = useState(null); // previous card closing after delay

  const toggleFlip = (cardId) => {
    if (flippedCard === cardId) {
      // Close same card immediately
      setFlippedCard(null);
      setClosingCard(null);
    } else {
      if (flippedCard !== null) {
        // Save previous card for delayed closing
        setClosingCard(flippedCard);
      }
      // Open new card instantly
      setFlippedCard(cardId);
    }
  };

  // Auto close previous card after 5 seconds
useEffect(() => {
  let timer;

  if (closingCard !== null) {
    timer = setTimeout(() => {
      setClosingCard(null);
    }, 5000); // 5 seconds
  } else if (flippedCard !== null) {
    timer = setTimeout(() => {
      setFlippedCard(null);
    }, 6000); // 1 second
  }

  return () => clearTimeout(timer);
}, [closingCard, flippedCard]);


  // Check if a card should be shown as flipped
  const isFlipped = (cardId) =>
    flippedCard === cardId || closingCard === cardId;

  const features = [
    {
      icon: <Briefcase className="w-6 h-6 text-blue-600" />,
      title: "Digital Transformation",
      subtitle: "Modernize operations and embrace innovation.",
      details:
        "Our digital transformation services help businesses leverage cutting-edge technologies to revolutionize their operations, improve customer experiences, and drive growth in the digital age. We focus on process optimization, technology integration, and cultural change management.",
    },
    {
      icon: <Network className="w-6 h-6 text-blue-600" />,
      title: "SAP Consulting",
      subtitle: "Streamline SAP workflows for better performance.",
      details:
        "Our SAP experts optimize your SAP environment to enhance efficiency, reduce costs, and improve decision-making through better data integration and process automation. We provide end-to-end SAP implementation and support services.",
    },
    {
      icon: <Database className="w-6 h-6 text-blue-600" />,
      title: "ERP/CRM Consulting",
      subtitle: "Integrate systems to boost efficiency and insights.",
      details:
        "We implement and optimize ERP and CRM systems that connect your business processes, providing real-time insights and improving customer relationships across all touchpoints. Our solutions ensure seamless data flow and enhanced productivity.",
    },
    {
      icon: <CloudCog className="w-6 h-6 text-blue-600" />,
      title: "Cloud & Security Consulting",
      subtitle: "Secure, scalable, and tailored cloud strategies.",
      details:
        "Our cloud solutions provide secure, flexible infrastructure with robust security measures to protect your data while enabling scalability and business continuity. We ensure compliance and optimize cloud costs for maximum ROI.",
    },
    {
      icon: <Settings className="w-6 h-6 text-blue-600" />,
      title: "IT Strategy & Infrastructure",
      subtitle: "Align tech infrastructure with your business goals.",
      details:
        "We design IT strategies and infrastructure that support your business objectives, ensuring technology investments deliver maximum value and competitive advantage. Our approach includes future-proofing and scalability planning.",
    },
  ];

  return (
    <div className="max-w-8xl mx-auto px-6 py-20 bg-blue-50 min-h-96">
      {/* Header Section */}
      <div className="text-center mb-10">
        <h2 className="text-4xl md:text-4xl font-bold text-gray-900 mb-2">
          Our <span className="text-blue-600">Consulting</span>
        </h2>
        <p className="text-gray-600 text-md max-w-5xl mx-auto leading-relaxed">
          Strategic, secure, and scalable consulting across Digital
          Transformation, ERP, SAP, Cloud, and more — tailored to elevate your
          business.
        </p>
      </div>

      {/* Top Row - 3 Cards */}
      <div className="flex justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 gap-8 mb-4 max-w-6xl w-full">
          {features.slice(0, 3).map((feature, index) => {
            const cardId = `top-${index}`;
            const flipped = isFlipped(cardId);

            return (
              <div
                key={index}
                className="relative w-full max-w-sm mx-auto h-64 perspective-1000"
              >
                <div
                  className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                    flipped ? "rotate-y-180" : ""
                  }`}
                  onClick={() => toggleFlip(cardId)}
                >
                  {/* Front Side */}
                  <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 flex flex-col justify-center items-center text-center border border-gray-100">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center mb-3 shadow-sm">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">
                      {feature.subtitle}
                    </p>
                    <div className="flex items-center text-blue-600 font-semibold text-sm">
                      <span>Learn more</span>
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </div>
                  </div>

                  {/* Back Side */}
                  <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl shadow-lg p-6 flex flex-col justify-center text-white">
                    <div className="flex-1 flex flex-col justify-center">
                      <h3 className="text-xl font-bold mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-blue-100 text-sm leading-relaxed mb-6">
                        {feature.details}
                      </p>
                    </div>
                    <div className="flex items-center text-blue-200 font-semibold text-sm justify-center">
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      <span>Back to overview</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Row - 2 Centered Cards */}
      <div className="flex flex-wrap justify-center gap-8 mb-2">
        {features.slice(3).map((feature, index) => {
          const cardId = `bottom-${index}`;
          const flipped = isFlipped(cardId);

          return (
            <div
              key={index}
              className="relative w-full max-w-sm h-64 perspective-1000"
            >
              <div
                className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
                  flipped ? "rotate-y-180" : ""
                }`}
                onClick={() => toggleFlip(cardId)}
              >
                {/* Front Side */}
                <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 flex flex-col justify-center items-center text-center border border-gray-100">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center mb-4 shadow-sm">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {feature.subtitle}
                  </p>
                  <div className="flex items-center text-blue-600 font-semibold text-sm">
                    <span>Learn more</span>
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>

                {/* Back Side */}
                <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl shadow-lg p-6 flex flex-col justify-center text-white">
                  <div className="flex-1 flex flex-col justify-center">
                    <h3 className="text-xl font-bold mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-blue-100 text-sm leading-relaxed mb-4">
                      {feature.details}
                    </p>
                  </div>
                  <div className="flex items-center text-blue-200 font-semibold text-sm justify-center">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    <span>Back to overview</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-preserve-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </div>
  );
}
